import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Signal, Radar, UtensilsCrossed, AlertTriangle } from 'lucide-react';
import { useOSPulse } from '../hooks/useOSPulse';
import { useT } from '../i18n';

/* AIMonitoringStrip — "AI is working right now." KITXENS signature strip.
   Powered by /api/pulse (real system activity + daily-growing baseline). */

export default function AIMonitoringStrip() {
  const pulse = useOSPulse();
  const { lang } = useT();
  const es = lang === 'es';
  const m = pulse?.monitoring;
  if (!m) return null;

  const stats = [
    { icon: Signal, v: m.websites_analyzed, l: es ? 'señales de restaurantes' : 'restaurant signals', k: 'restaurant_signals' },
    { icon: Radar, v: m.ai_search_updates, l: es ? 'actualizaciones de AI Search' : 'AI Search updates', k: 'ai_search_updates' },
    { icon: UtensilsCrossed, v: m.menu_changes, l: es ? 'cambios de menú rastreados' : 'menu changes tracked', k: 'menu_changes' },
    { icon: AlertTriangle, v: m.industry_alerts, l: es ? 'alertas de industria' : 'industry alerts', k: 'industry_alerts' },
  ].filter((s) => s.v != null);

  return (
    <section className="border-y border-neutral-200 bg-neutral-50/70" data-testid="ai-monitoring-strip">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="shrink-0 lg:pr-6 lg:border-r lg:border-neutral-200 max-w-[220px]">
            <p className="inline-flex items-center gap-2 text-[13px] font-bold text-neutral-900">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
                <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              {es ? 'La IA está trabajando ahora mismo.' : 'AI is working right now.'}
            </p>
            <p className="mt-1 text-[11px] text-neutral-400">
              {es ? 'Monitoreando · Actualizado cada 5 minutos' : 'Monitoring · Updated every 5 minutes'}
            </p>
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map(({ icon: Icon, v, l, k }, i) => (
              <motion.div
                key={l}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-3"
                data-testid={`ai-monitoring-counter-${k}`}
              >
                <span className="inline-flex w-9 h-9 rounded-xl bg-white border border-neutral-200 items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#F77F11]" />
                </span>
                <p className="text-[12.5px] text-neutral-600 leading-snug">
                  {v > 0 ? (
                    <span className="block text-[17px] font-bold text-neutral-900 tracking-[-0.01em] tabular-nums">{Number(v).toLocaleString()}</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-600" data-testid={`ai-monitoring-standby-${k}`}>
                      <span className="relative flex w-1.5 h-1.5">
                        <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
                        <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      </span>
                      {es ? 'En espera de datos' : 'Awaiting live data'}
                    </span>
                  )}
                  {l}
                </p>
              </motion.div>
            ))}
          </div>
          <Link
            to="/intelligence"
            className="shrink-0 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#F77F11] hover:text-[#d8651a] transition-colors"
            data-testid="ai-monitoring-link"
          >
            {es ? 'Ver el Intelligence Hub' : 'See the Intelligence Hub'} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
